/*
 * Copyright (c) 2022 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { ChangeDetectorRef, Component, Input, output } from '@angular/core';
import { AddressSearchResultMapper } from '../../g-map-result-mapper/address-search-result.mapper';
import { CommonModule } from '@angular/common';
import { GoogleAddressListComponent } from '../google-address-list/google-address-list.component';
import { TranslateModule } from '@ngx-translate/core';
import { AddressDTO } from '@well/address-management-domain-model';

@Component({
  selector: 'well-google-address-search',
  templateUrl: './google-address-search.component.html',
  styleUrls: ['./google-address-search.component.scss'],
  standalone: true,
  imports: [CommonModule, GoogleAddressListComponent, TranslateModule],
})
export class GoogleAddressSearchComponent {
  private _searchTerm: string | undefined = undefined;
  private service: google.maps.places.AutocompleteService | undefined = undefined;
  private sessionToken: google.maps.places.AutocompleteSessionToken | undefined = undefined;
  private placeService: google.maps.places.PlacesService | undefined = undefined;
  predictions: google.maps.places.AutocompletePrediction[] | undefined = undefined;

  addressSelect = output<AddressDTO>();

  status:
    | google.maps.places.PlacesServiceStatus.INVALID_REQUEST
          | google.maps.places.PlacesServiceStatus.NOT_FOUND
          | google.maps.places.PlacesServiceStatus.OVER_QUERY_LIMIT
          | google.maps.places.PlacesServiceStatus.REQUEST_DENIED
          | google.maps.places.PlacesServiceStatus.UNKNOWN_ERROR
          | google.maps.places.PlacesServiceStatus.ZERO_RESULTS | undefined = undefined;

  @Input() set searchTerm(value: string) {
    this._searchTerm = value;
    this.search(value);
    this.changeDetectorRef.markForCheck();
  }

  private _map: boolean | null = null;
  private pendingValue: string | null = null;

  @Input() set map(map: boolean) {
    this._map = map;
    if (this._map) {
      this.service = new google.maps.places.AutocompleteService();
      this.sessionToken = new google.maps.places.AutocompleteSessionToken();
      const div = document.createElement('div');
      this.placeService = new google.maps.places.PlacesService(div);

      if (this.pendingValue) {
        this.search(this.pendingValue);
        this.pendingValue = null;
      }
    }
  }

  get map(): boolean | null {
    return this._map;
  }

  constructor(private changeDetectorRef: ChangeDetectorRef) {}

  private search(value: string) {
    if (!this.map) {
      this.pendingValue = value;
      return;
    }

    if (this.service && value) {
      this.service.getPlacePredictions(
        {
          input: value,
          sessionToken: this.sessionToken,
        },
        (predictions, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK) {
            this.predictions = [...predictions];
            this.changeDetectorRef.detectChanges();
            this.status = null;
          } else {
            this.predictions = [];
            this.status = status;
          }
        }
      );
    }
  }

  onSelect(place: google.maps.places.AutocompletePrediction) {
    this.placeService?.getDetails(
      {
        placeId: place.place_id,
        sessionToken: this.sessionToken,
      },
      (placeResult: google.maps.places.PlaceResult) => {
        if (placeResult.geometry === undefined || placeResult.geometry === null) {
          return;
        }

        const resultMapper: AddressSearchResultMapper = new AddressSearchResultMapper(placeResult);
        const result = resultMapper.getAddress();
        this.addressSelect.emit(result);
      }
    );
  }
}
